export type AppScope = "trial" | "portfolio" | "admin" | "global";

export function isScopedAppScope(
  scope: string | null | undefined,
): scope is Exclude<AppScope, "admin" | "global"> {
  return scope === "trial" || scope === "portfolio";
}

export function isAppScope(
  scope: string | null | undefined,
): scope is AppScope {
  return scope === "trial" || scope === "portfolio" || scope === "admin";
}

export function getScopeFromPathname(
  pathname: string,
  fallbackScope?: string | null,
): AppScope {
  const [firstSegment = ""] = pathname.split("/").filter(Boolean);

  if (isAppScope(firstSegment)) {
    return firstSegment;
  }

  if (isAppScope(fallbackScope)) {
    return fallbackScope;
  }

  return "global";
}

export function getScopeId(
  scope: AppScope,
  paramsId?: string,
  pathname?: string,
): string {
  if (!isScopedAppScope(scope)) return "";

  const pathSegments = pathname?.split("/").filter(Boolean) ?? [];
  return paramsId || pathSegments[1] || "";
}

/**
 * The trial the current URL is about, or "" when there is not one.
 *
 * Trial scope carries the trial as the scope id. Trial *detail* screens are
 * registered under the portfolio scope (`/portfolio/trials/<trialId>/...`), so
 * `getScopeId` reports "trials" there and the trial sits one segment further
 * along. Every other portfolio screen is genuinely cross-trial and yields "".
 *
 * Read from the pathname rather than `useParams` so it also works above the
 * matched route — the Ask Vivo provider sits over the whole router, where route
 * params are not in scope.
 */
export function getTrialIdFromPathname(pathname: string): string {
  const [first, second, third] = pathname.split("/").filter(Boolean);

  if (first === "trial") return second ?? "";
  if (first === "portfolio" && second === "trials") return third ?? "";
  return "";
}

export function buildScopedUrl(
  scope: AppScope,
  id: string,
  path = "",
  globalBase = "",
): string {
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  if (cleanPath === "wiki" || cleanPath.startsWith("wiki/")) {
    return `/${cleanPath}`;
  }

  if (scope === "global") {
    return cleanPath ? `/${cleanPath}` : globalBase ? `/${globalBase}` : "/";
  }

  if (scope === "admin") {
    return cleanPath ? `/admin/${cleanPath}` : "/admin";
  }

  if (scope === "portfolio") {
    return cleanPath ? `/${scope}/${cleanPath}` : `/${scope}`;
  }

  return cleanPath ? `/${scope}/${id}/${cleanPath}` : `/${scope}/${id}`;
}

export function getScopePathSegmentIndex(scope: AppScope): number {
  if (scope === "admin") return 2;
  if (scope === "portfolio") return 2;
  if (scope === "trial") return 3;
  return 1;
}
